import React, { useState } from "react";
import * as Types from "../types/BaseTypes";
import Tooltip from "./common/Tooltip";
import { Portal } from "react-portal";
import Info from "../images/info";
import Disclaimer from "../objects/Disclaimer";

import Classification from "../objects/Classification";
import Category from "../objects/Category";
import Refrigerant from "../objects/Fluids";

import Slider from "./Slider";

import {
  FlexibleXYPlot,
  XAxis,
  YAxis,
  GradientDefs,
  MarkSeries,
  AreaSeries,
  LineSeries,
  LabelSeries,
} from "react-vis";
import Fluids from "../objects/Fluids";

interface FixedValues {
  c1: string;   // Value for EN 378 Appendix C1
  c2: string;   // Value for EN 378 Appendix C2
  qlmv: string;
  qlav: string;
}

interface Chart {
  calculations: any;
  estimate: number;
  core: Types.Core;
  setCore: Function;
  base: any;
  room: any;
  data: any;
  tooltip: string;
  displayTooltip: Function;
  setTooltip: Function;
  onClick: Function;
  prev: Function;
  factor: Types.HeightFactor;
}

export default ({
  calculations,
  estimate,
  core,
  setCore,
  base,
  room,
  data,
  tooltip,
  displayTooltip,
  setTooltip,
  onClick,
  prev,
  factor,
}: Chart) => {
  const [sealedPopup, setSealedPopup] = useState(false);
  const [popup, setPopup] = useState(false);

  const getSliderLeft = () => {
    switch (core.refrigerant) {
      case Types.Refrigerant.XL10:
        return 0;
      case Types.Refrigerant.XL20:
        return 1;
      case Types.Refrigerant.XL40:
        return 2;
      case Types.Refrigerant.XL41:
        return 3;
      default:
        return 0;
    }
  };

  const [panelDirection, setPanelDirection] = useState(100);
  const newPanelDirection = (input: Types.Category) => {
    setPanelDirection(core.category > input ? 100 : -100);
  };

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const onMouseMove = (e: any) => {
    tooltip !== "" && setMousePos({ x: e.clientX, y: e.clientY });
  };

  const formatChargeRange = (val: string) => {
    if (val === "N/A") return "N/A";
    if (val === "No Charge Restriction") return "No Charge Restriction";
    return `0.00 kg – ${val}`;
  };


const getInstallationLabel = () => {
    switch (factor) {
      // "Floor" is likely named "FloorMounted" to match the others
      case Types.HeightFactor.FloorLocation:
        return "Floor Location 0.6m";
      
      // "Window" is likely named "WindowMounted"
      case Types.HeightFactor.WindowMounted:
        return "Window Mounted 1m";
        
      case Types.HeightFactor.WallMounted:
        return "Wall Mounted 1.8m";
        
      case Types.HeightFactor.CeilingMounted:
        return "Ceiling Mounted 2.2m";
        
      default:
        return "Unknown";
    }
  };

  /**
   * Returns either a string like "1.76 kg", "No Charge Restriction", or "N/A".
   */
  const showDataRange = (props: { sealed: boolean; queue: string }) => {
    const { sealed, queue } = props;
    let appl = "OA";
    switch (core.application) {
      case Types.Application.OtherApplications:
        appl = "OA";
        break;
      case Types.Application.LessThanOnePerson:
        appl = "LO";
        break;
      case Types.Application.HumanComfort:
        appl = "HC";
        break;
    }

    let clas = "i";
    switch (core.classification) {
      case Types.Classification.I:
        clas = "i";
        break;
      case Types.Classification.II:
        clas = "ii";
        break;
      case Types.Classification.III:
        clas = "iii";
        break;
      case Types.Classification.IV:
        clas = "iv";
        break;
    }

    let cate = "a";
    switch (core.category) {
      case Types.Category.a:
        cate = "a";
        break;
      case Types.Category.b:
        cate = "b";
        break;
      case Types.Category.c:
        cate = "c";
        break;
    }
    try {
      // For Human Comfort, "c1" is replaced with "c2" in the original logic
      if (appl === "HC") {
        const r1 = +calculations[appl][cate][clas][
          queue === "c1" ? "c2" : queue
        ].toFixed(2);
        return r1 === 0 ? "No Charge Restriction" : r1.toFixed(2) + " kg";
      }

      if (sealed) {
        const r2 = +calculations[appl + "s"][cate][clas][queue].toFixed(2);
        return r2 === 0 ? "No Charge Restriction" : r2.toFixed(2) + " kg";
      } else {
        const r3 = +calculations[appl + "n"][cate][clas][queue].toFixed(2);
        return r3 === 0 ? "No Charge Restriction" : r3.toFixed(2) + " kg";
      }
    } catch (err) {
      return "N/A";
    }
  };

  const UFL_VALUES = {
    XL10: 0.573,
    XL20: 0.569,
    XL40: 0.522,
    XL41: 0.569,
  };

  const getUFL = (refrigerant: Types.Refrigerant) => {
    switch (refrigerant) {
      case Types.Refrigerant.XL10:
        return UFL_VALUES.XL10;
      case Types.Refrigerant.XL20:
        return UFL_VALUES.XL20;
      case Types.Refrigerant.XL40:
        return UFL_VALUES.XL40;
      case Types.Refrigerant.XL41:
        return UFL_VALUES.XL41;
      default:
        return "N/A";
    }
  };

  /**
   * The "raw" version returns a number only, used for calculations.
   */
  const showDataRangeraw = (props: { sealed: boolean; queue: string }) => {
    const { sealed, queue } = props;
    let appl = "OA";
    switch (core.application) {
      case Types.Application.OtherApplications:
        appl = "OA";
        break;
      case Types.Application.LessThanOnePerson:
        appl = "LO";
        break;
      case Types.Application.HumanComfort:
        appl = "HC";
        break;
    }

    let clas = "i";
    switch (core.classification) {
      case Types.Classification.I:
        clas = "i";
        break;
      case Types.Classification.II:
        clas = "ii";
        break;
      case Types.Classification.III:
        clas = "iii";
        break;
      case Types.Classification.IV:
        clas = "iv";
        break;
    }

    let cate = "a";
    switch (core.category) {
      case Types.Category.a:
        cate = "a";
        break;
      case Types.Category.b:
        cate = "b";
        break;
      case Types.Category.c:
        cate = "c";
        break;
    }
    try {
      if (appl === "HC") {
        const r1 = +calculations[appl][cate][clas][
          queue === "c1" ? "c2" : queue
        ].toFixed(2);
        return r1;
      }

      if (sealed) {
        const r2 = +calculations[appl + "s"][cate][clas][queue].toFixed(2);
        return r2;
      } else {
        const r3 = +calculations[appl + "n"][cate][clas][queue].toFixed(2);
        return r3;
      }
    } catch (err) {
      return 0;
    }
  };

  const isOneDecimal = (value: string | number) => {
    try {
      if (value.toString().split(".")[1].length === 1) {
        return value + "0";
      }
    } catch (err) {
      return value + ".00";
    }
    return value;
  };

  const getC31 = (isSealed: boolean): C31 => {
    const c1 = +showDataRangeraw({ sealed: isSealed, queue: "c1" });
    const qlmv = +showDataRangeraw({ sealed: isSealed, queue: "qlmv" });
    if (!Number.isFinite(c1) || !Number.isFinite(qlmv) || qlmv <= c1) return { na: true };
    return { na: false, min: +(c1 + 0.01).toFixed(2), max: +(qlmv - 0.01).toFixed(2) };
  };

  type C31 = { na: true } | { na: false; min: number; max: number };


  const renderTable = () => {
    // Destructure necessary variables from 'core' and 'room'
    const { classification, application, category, refrigerant } = core;
    const { cubic } = room;

    const isWallMounted1_8m = factor === Types.HeightFactor.WallMounted;
    const isCeilingMounted2_2m = factor === Types.HeightFactor.CeilingMounted;

    const isClassOne = classification === Types.Classification.I;
    const isRoomTooSmall =
      classification === Types.Classification.I && cubic < 12.1;



    // For quickly reading raw data:
    const qlmvRaw = showDataRangeraw({ sealed: true, queue: "qlmv" });
    const qlavRaw = showDataRangeraw({ sealed: true, queue: "qlav" });
    const c2Raw = showDataRangeraw({ sealed: true, queue: "c2" });

    const shouldApplyWallMountedRule =
      application === Types.Application.HumanComfort &&
      classification === Types.Classification.II &&
      isWallMounted1_8m &&
      qlmvRaw < qlavRaw &&
      cubic < 49;

    const shouldApplyCeilingMountedRule =
      application === Types.Application.HumanComfort &&
      classification === Types.Classification.II &&
      isCeilingMounted2_2m &&
      qlmvRaw < qlavRaw &&
      cubic <
        (() => {
          switch (refrigerant) {
            case Types.Refrigerant.XL10:
              return 70;
            case Types.Refrigerant.XL20:
              return 73;
            case Types.Refrigerant.XL40:
              return 74;
            case Types.Refrigerant.XL41:
              return 69;
            default:
              return 73;
          }
        })();

    // For sealed-only special-case renders
    const renderSealedSystemOnly = (fixedValues: FixedValues, appendix: string) => {
      const cValue = appendix === "C2" ? fixedValues.c2 : fixedValues.c1;
      return (
        <table className="results-table" style={{ textAlign: "left" }}>
          <tbody>
            <tr>
              <td onClick={() => setSealedPopup(true)} className="t-main-title">
                SEALED SYSTEM <Info className="print-hide" />
              </td>
            </tr>
            <tr>
              <td className="t-segment-title">{`EN 378 Appendix ${appendix}`}</td>
            </tr>
            <tr>
              <td className="t-segment-value">{cValue}</td>
            </tr>
            <tr>
              <td onClick={() => setPopup(true)} className="t-segment-title">
                EN 378 Appendix C3 (QLMV) <Info className="print-hide" />
              </td>
            </tr>
            <tr>
              <td className="t-segment-value">{fixedValues.qlmv}</td>
            </tr>
            <tr>
              <td onClick={() => setPopup(true)} className="t-segment-title">
                EN 378 Appendix C3 (QLAV) <Info className="print-hide" />
              </td>
            </tr>
            <tr>
              <td className="t-segment-value">{fixedValues.qlav}</td>
            </tr>
          </tbody>
        </table>
      );
    };

    // For rendering both sealed & non-sealed systems
    const renderBothSystems = (fixedValues: FixedValues) => {
      const c31NonSealed = getC31(false);
      
      // NEW LOGIC: Retrieve raw values for Non-Sealed
      const qlmvRawNonSealed = +showDataRangeraw({ sealed: false, queue: "qlmv" });
      const c1RawNonSealed = +showDataRangeraw({ sealed: false, queue: "c1" });

      return (
      <table className="results-table" style={{ textAlign: "left" }}>
        <tbody>
          <tr>
            <td onClick={() => setSealedPopup(true)} className="t-main-title">
              SEALED SYSTEM <Info className="print-hide" />
            </td>
            <td className="t-main-title">NON-SEALED SYSTEM</td>
          </tr>

          {/* EN 378 Appendix C1 */}
          <tr>
            <td className="t-segment-title">EN 378 Appendix C1</td>
            <td className="t-segment-title">EN 378 Appendix C1</td>
          </tr>
          <tr>
            <td className="t-segment-value">
              {formatChargeRange(showDataRange({ sealed: true, queue: "c1" }))}
            </td>
            <td className="t-segment-value">
              {formatChargeRange(showDataRange({ sealed: false, queue: "c1" }))}
            </td>
          </tr>

          {/* QLMV */}
          <tr>
            <td onClick={() => setPopup(true)} className="t-segment-title">
              EN 378 Appendix C3 (QLMV) <Info className="print-hide" />
            </td>
            <td onClick={() => setPopup(true)} className="t-segment-title">
              EN 378 Appendix C3 (QLMV) <Info className="print-hide" />
            </td>
          </tr>
          <tr>
            <td className="t-segment-value">{fixedValues.qlmv}</td>
            <td className="t-segment-value">
               {/* CONDITIONAL LOGIC: If QLMV_raw < C1_raw, use QLMV_raw. Else enforce gap. */}
              {isRoomTooSmall ||
              isClassOne ||
              showDataRange({ sealed: false, queue: "qlmv" }) === "N/A"
                ? "N/A"
                : `${(qlmvRawNonSealed < c1RawNonSealed 
                    ? qlmvRawNonSealed 
                    : Math.max(qlmvRawNonSealed, +(c1RawNonSealed + 0.01).toFixed(2))
                  ).toFixed(2)} kg - ${showDataRange({
                    sealed: false,
                    queue: "qlav",
                  })}`}
            </td>
          </tr>

          {/* QLAV */}
          <tr>
            <td onClick={() => setPopup(true)} className="t-segment-title">
              EN 378 Appendix C3 (QLAV) <Info className="print-hide" />
            </td>
            <td onClick={() => setPopup(true)} className="t-segment-title">
              EN 378 Appendix C3 (QLAV) <Info className="print-hide" />
            </td>
          </tr>
          <tr>
            <td className="t-segment-value">{fixedValues.qlav}</td>
            <td className="t-segment-value">
              {isRoomTooSmall ||
              isClassOne ||
              showDataRange({ sealed: false, queue: "qlav" }) === "N/A"
                ? "N/A"
                : `${
                    // FIX: Use Math.round(x * 100) + 1 to avoid floating point issues with 1.12 + 0.01 -> 1.14
                    (Math.round(
                      (parseFloat(showDataRange({ sealed: false, queue: "qlav" }) || "0")
                    ) * 100) + 1) / 100
                   } kg - ${
                    base.m.m3
                      ? `${Math.ceil(parseFloat(base.m.m3) * 100) / 100} kg`
                      : "N/A"
                  }`}
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

    

    // HUMAN COMFORT (sealed system logic)
    if (application === Types.Application.HumanComfort) {
      if (shouldApplyWallMountedRule) {
        return (
          <table className="results-table" style={{ textAlign: "left" }}>
            <tbody>
              <tr>
                <td onClick={() => setSealedPopup(true)} className="t-main-title">
                  SEALED SYSTEM <Info className="print-hide" />
                </td>
              </tr>
              <tr>
                <td className="t-segment-title">EN 378 Appendix C2</td>
              </tr>
              <tr>
                <td className="t-segment-value">
                  {formatChargeRange(showDataRange({ sealed: true, queue: "c2" }))}
                </td>
              </tr>
              <tr>
                <td onClick={() => setPopup(true)} className="t-segment-title">
                  EN 378 Appendix C3 (QLMV) <Info className="print-hide" />
                </td>
              </tr>
              <tr>
                {/* FIXED: Manually calculate range to avoid starting from 0.00 if unnecessary */}
                {/* UPDATED: End value is now qlavRaw - 0.01 to prevent overlap */}
                <td className="t-segment-value">
                  {(() => {
                    const startVal = qlmvRaw < c2Raw 
                      ? qlmvRaw 
                      : Math.max(qlmvRaw, +(c2Raw + 0.01).toFixed(2));
                    
                    const endVal = +(qlavRaw - 0.01).toFixed(2);
                    
                    // Clamp to the calculated end value to avoid logical errors
                    const validStart = Math.min(startVal, endVal);
                    
                    return `${validStart.toFixed(2)} kg - ${endVal.toFixed(2)} kg`;
                  })()}
                </td>
              </tr>
              <tr>
                <td onClick={() => setPopup(true)} className="t-segment-title">
                  EN 378 Appendix C3 (QLAV) <Info className="print-hide" />
                </td>
              </tr>
              <tr>
                <td className="t-segment-value">
                  {/* MODIFIED: Use qlavRaw as start instead of c2Raw + 0.01 */}
                  {`${qlavRaw.toFixed(2)} kg - ${
                    base.m.m3
                      ? `${Math.ceil(parseFloat(base.m.m3) * 100) / 100} kg`
                      : "N/A"
                  }`}
                </td>
              </tr>
            </tbody>
          </table>
        );
      }

      if (shouldApplyCeilingMountedRule) {
        return (
          <table className="results-table" style={{ textAlign: "left" }}>
            <tbody>
                   <tr>
                <td onClick={() => setSealedPopup(true)} className="t-segment-title">
                  SEALED SYSTEM <Info className="print-hide" />
                </td>
              </tr>
              <tr>
                <td className="t-segment-title">EN 378 Appendix C2</td>
              </tr>
              <tr>
                <td className="t-segment-value">
                  {formatChargeRange(showDataRange({ sealed: true, queue: "c2" }))}
                </td>
              </tr>
              <tr>
                <td onClick={() => setPopup(true)} className="t-segment-title">
                  EN 378 Appendix C3 (QLMV) <Info className="print-hide" />
                </td>
              </tr>
              <tr>
                {/* FIXED: Manually calculate range to avoid starting from 0.00 if unnecessary */}
                {/* UPDATED: End value is now qlavRaw - 0.01 to prevent overlap */}
                <td className="t-segment-value">
                  {(() => {
                    const startVal = qlmvRaw < c2Raw 
                      ? qlmvRaw 
                      : Math.max(qlmvRaw, +(c2Raw + 0.01).toFixed(2));
                    
                    const endVal = +(qlavRaw - 0.01).toFixed(2);

                    const validStart = Math.min(startVal, endVal);

                    return `${validStart.toFixed(2)} kg - ${endVal.toFixed(2)} kg`;
                  })()}
                </td>
              </tr>
              <tr>
                <td onClick={() => setPopup(true)} className="t-segment-title">
                  EN 378 Appendix C3 (QLAV) <Info className="print-hide" />
                </td>
              </tr>
              <tr>
                <td className="t-segment-value">
                  {/* MODIFIED: Use qlavRaw as start instead of c2Raw + 0.01 */}
                  {`${qlavRaw.toFixed(2)} kg - ${
                    base.m.m3
                      ? `${Math.ceil(parseFloat(base.m.m3) * 100) / 100} kg`
                      : "N/A"
                  }`}
                </td>
              </tr>
            </tbody>
          </table>
        );
      }

      // Default sealed system table for Human Comfort
      return (
        <table className="results-table" style={{ textAlign: "left" }}>
          <tbody>
            <tr>
              <td onClick={() => setSealedPopup(true)} className="t-main-title">
                SEALED SYSTEM <Info className="print-hide" />
              </td>
            </tr>
            <tr>
              <td className="t-segment-title">EN 378 Appendix C2</td>
            </tr>
            <tr>
              <td className="t-segment-value">
                {formatChargeRange(
                  showDataRange({ sealed: true, queue: "c2" })
                )}
              </td>
            </tr>
            <tr>
              <td onClick={() => setPopup(true)} className="t-segment-title">
                EN 378 Appendix C3 (QLMV) <Info className="print-hide" />
              </td>
            </tr>
            <tr>
              <td className="t-segment-value">
                {/* QLMV Clamping Logic */}
                {isRoomTooSmall ||
                isClassOne ||
                showDataRange({ sealed: true, queue: "qlmv" }) === "N/A"
                  ? "N/A"
                  : (() => {
                      const startVal =
                        qlmvRaw < c2Raw
                          ? qlmvRaw
                          : Math.max(qlmvRaw, +(c2Raw + 0.01).toFixed(2));
                      // If the start range exceeds the limit (qlavRaw), clamp it to qlavRaw
                      const finalStart =
                        startVal > qlavRaw ? qlavRaw : startVal;

                      return `${finalStart.toFixed(2)} kg - ${showDataRange({
                        sealed: true,
                        queue: "qlav",
                      })}`;
                    })()}
              </td>
            </tr>
            <tr>
              <td onClick={() => setPopup(true)} className="t-segment-title">
                EN 378 Appendix C3 (QLAV) <Info className="print-hide" />
              </td>
            </tr>
            <tr>
              <td className="t-segment-value">
                {/* MODIFIED: QLAV Clamping Logic to prevent Start > End */}
                {isRoomTooSmall ||
                isClassOne ||
                showDataRange({ sealed: true, queue: "qlav" }) === "N/A"
                  ? "N/A"
                  : (() => {
                      // Original Start Calculation: Round(QLAV * 100 + 1) / 100 -> effectively QLAV + 0.01
                      const qlavRawVal = parseFloat(showDataRange({ sealed: true, queue: "qlav" }) || "0");
                      const rawStart = (Math.round(qlavRawVal * 100 + 1) / 100);
                      
                      // End limit is base.m.m3
                      const endLimit = base.m.m3 ? Math.ceil(parseFloat(base.m.m3) * 100) / 100 : 0;
                      
                      // Check for clamp
                      const finalStart = (base.m.m3 && rawStart > endLimit) ? endLimit : rawStart;

                      return `${finalStart.toFixed(2)} kg - ${
                        base.m.m3
                          ? `${endLimit.toFixed(2)} kg`
                          : "N/A"
                      }`;
                    })()
                }
              </td>
            </tr>
          </tbody>
        </table>
      
      );
    }

    // OTHER APPLICATIONS or <1 PERSON
    if (
      application === Types.Application.OtherApplications ||
      application === Types.Application.LessThanOnePerson
    ) {
      

      const c31 = getC31(true);
      
      // NEW LOGIC: Retrieve raw values for Sealed
      const qlmvRawSealed = +showDataRangeraw({ sealed: true, queue: "qlmv" });
      const c1RawSealed = +showDataRangeraw({ sealed: true, queue: "c1" });

      const fixedValuesOtherApplications: FixedValues = {
        c1: formatChargeRange(showDataRange({ sealed: true, queue: "c1" })),
        c2: formatChargeRange(showDataRange({ sealed: true, queue: "c2" })),
        qlmv:
          isClassOne || showDataRange({ sealed: true, queue: "qlmv" }) === "N/A"
            ? "N/A"
            : `${
                // CONDITIONAL LOGIC: If QLMV_raw < C1_raw, use QLMV_raw. Else enforce gap.
                (qlmvRawSealed < c1RawSealed 
                  ? qlmvRawSealed 
                  : Math.max(qlmvRawSealed, +(c1RawSealed + 0.01).toFixed(2))
                ).toFixed(2)
              } kg - ${showDataRange({ sealed: true, queue: "qlav" })}`,
        qlav:
          isClassOne || showDataRange({ sealed: true, queue: "qlav" }) === "N/A"
            ? "N/A"
            : `${
                // FIX: Use Math.round(x * 100) + 1
                (Math.round(
                    (parseFloat(showDataRange({ sealed: true, queue: "qlav" }) || "0")
                  ) * 100) + 1) / 100
                } kg - ${
                base.m.m3 ? `${Math.ceil(parseFloat(base.m.m3) * 100) / 100} kg` : "N/A"
              }`,
      };

      // If room volume > 29.9 m³, show ONLY sealed system
      if (room.cubic > 29.9) {
        return renderSealedSystemOnly(fixedValuesOtherApplications, "C1");
      }

      // Otherwise, show both sealed & non-sealed
      return renderBothSystems(fixedValuesOtherApplications);
    }

    // If we get here, just a fallback:
    const qlmvSealedValue =
      showDataRange({ sealed: true, queue: "qlmv" }) === "N/A"
        ? "N/A"
        : `${(
            parseFloat(showDataRange({ sealed: true, queue: "c1" })) + 0.01
          ).toFixed(2)} kg - ${showDataRange({
            sealed: true,
            queue: "qlav",
          })}`;

    const qlmvNonSealedValue =
      showDataRange({ sealed: false, queue: "qlmv" }) === "N/A"
        ? "N/A"
        : `${(
            parseFloat(showDataRange({ sealed: false, queue: "qlmv" })) + 0.01
          ).toFixed(2)} kg - ${showDataRange({
            sealed: false,
            queue: "qlav",
          })}`;

    let qlavSealedValue =
      showDataRange({ sealed: true, queue: "qlav" }) === "N/A"
        ? "N/A"
        : `${(
            // FIX: Use Math.round(x * 100) + 1
            (Math.round(
              (parseFloat(
                showDataRange({ sealed: true, queue: "qlav" }) || "0"
              )
            ) * 100) + 1) / 100
          ).toFixed(2)} kg - ${
            base.m.m3
              ? `${Math.ceil(parseFloat(base.m.m3) * 100) / 100} kg`
              : "N/A"
          }`;

    let qlavNonSealedValue =
      showDataRange({ sealed: false, queue: "qlav" }) === "N/A"
        ? "N/A"
        : `${(
             // FIX: Use Math.round(x * 100) + 1
            (Math.round(
              (parseFloat(
                showDataRange({ sealed: false, queue: "qlav" }) || "0"
              )
            ) * 100) + 1) / 100
          ).toFixed(2)} kg - ${
            base.m.m3
              ? `${Math.ceil(parseFloat(base.m.m3) * 100) / 100} kg`
              : "N/A"
          }`;

    // If Class I, we override:
    if (isClassOne) {
      qlavSealedValue = "N/A";
      qlavNonSealedValue = "N/A";
      // Similarly for QLMV in Class I:
      // (Depending on your logic you might do the same override)
    }

    return (
      <table className="results-table" style={{ textAlign: "left" }}>
        <tbody>
          <tr>
            <td onClick={() => setSealedPopup(true)} className="t-main-title">
              SEALED SYSTEM <Info className="print-hide" />
            </td>
            <td className="t-main-title">NON-SEALED SYSTEM</td>
          </tr>
          <tr>
            <td className="t-segment-title">EN 378 Appendix C1</td>
            <td className="t-segment-title">EN 378 Appendix C1</td>
          </tr>
          <tr>
            <td className="t-segment-value">
              {formatChargeRange(showDataRange({ sealed: true, queue: "c1" }))}
            </td>
            <td className="t-segment-value">
              {formatChargeRange(showDataRange({ sealed: false, queue: "c1" }))}
            </td>
          </tr>
          <tr>
            <td onClick={() => setPopup(true)} className="t-segment-title">
              EN 378 Appendix C3 (QLMV) <Info className="print-hide" />
            </td>
            <td onClick={() => setPopup(true)} className="t-segment-title">
              EN 378 Appendix C3 (QLMV) <Info className="print-hide" />
            </td>
          </tr>
          <tr>
            <td className="t-segment-value">{qlmvSealedValue}</td>
            <td className="t-segment-value">{qlmvNonSealedValue}</td>
          </tr>
          <tr>
            <td onClick={() => setPopup(true)} className="t-segment-title">
              EN 378 Appendix C3 (QLAV) <Info className="print-hide" />
            </td>
            <td onClick={() => setPopup(true)} className="t-segment-title">
              EN 378 Appendix C3 (QLAV) <Info className="print-hide" />
            </td>
          </tr>
          <tr>
            <td className="t-segment-value">{qlavSealedValue}</td>
            <td className="t-segment-value">{qlavNonSealedValue}</td>
          </tr>
        </tbody>
      </table>
    );
  };
  const formatRoomVolume = (value: number) => {
    return value 
  };
  const getXAxisValueForLabels = () =>
    core.application === Types.Application.HumanComfort ? room.square : room.cubic;
  const calculateOffset = (value: number) => value;
  const getXAxisValueForGLine = () =>
    core.application === Types.Application.HumanComfort ? room.square : room.cubic;
  const cappedVolume = room.cubic > 1000 ? 1000 : room.cubic;
  const getXAxisValue = () =>
    core.application === Types.Application.HumanComfort ? room.square : cappedVolume;

  // Separate the xDomain logic for each case:
  const xDomain = (() => {
    if (core.application === Types.Application.HumanComfort) {
      return undefined; // Use auto domain (or based on room.square) for Human Comfort
    }
    if (room.cubic >= 1000) {
      return undefined;
    }
    if (data.F[0].x > 0.5) {
      return [data.F[0].x - 2, data.F[data.F.length - 1].x];
    }
    return undefined;
  })();

  return (
    <div
      className={"page p-result"}
      onMouseEnter={() => setTooltip("")}
      onMouseLeave={() => setTooltip("")}
      onMouseMove={(e: any) => onMouseMove(e)}
      style={{ maxWidth: 1000 }}
    >
      <Slider
        core={{ get: core, set: (e: Types.Core) => setCore(e) }}
        type={core.refrigerant}
        typePrimitive={"refrigerant"}
        data={[
          {
            value: Types.Refrigerant.XL10,
            title: "Opteon™ XL10",
            mobile: "XL10",
          },
          {
            value: Types.Refrigerant.XL20,
            title: "Opteon™ XL20",
            mobile: "XL20",
          },
          {
            value: Types.Refrigerant.XL40,
            title: "Opteon™ XL40",
            mobile: "XL40",
          },
          {
            value: Types.Refrigerant.XL41,
            title: "Opteon™ XL41",
            mobile: "XL41",
          },
        ]}
        getSliderLeft={() => getSliderLeft()}
        newPanelDirection={(e: any) => newPanelDirection(e)}
        style={{ maxWidth: 800 }}
        mobileWidth={700}
      />

      <div
        className={"details-panel"}
        style={{
          padding: 0,
          display: "flex",
          flexFlow: "column",
          maxWidth:
            showDataRange({ sealed: true, queue: "c1" }) !==
              "No Charge Restriction" &&
            core.classification !== Types.Classification.IV
              ? "calc(1000px - 60px)"
              : 500,
          width: "auto",
        }}
        onMouseEnter={() => setTooltip("")}
      >
        {core.application === Types.Application.HumanComfort &&
          core.classification === Types.Classification.I &&
          estimate > calculations.OR.c22_c3.min &&
          estimate <= calculations.OR.c22_c3.max && (
            <div
              className="info-extra-hc"
              style={{ display: "flex", flexFlow: "row" }}
            >
              <div style={{ display: "flex", flexFlow: "row", padding: 15 }}>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "row",
                    fontSize: 18,
                    fontWeight: 100,
                    whiteSpace: "nowrap",
                  }}
                >
                  <span style={{ margin: "auto" }}>
                    {isOneDecimal(+calculations.OR.c22_c4.max.toFixed(2))} kg
                  </span>
                </div>
                <div style={{ fontSize: 12, margin: "auto 0 auto 10px" }}>
                  Maximum Charge for a Non-Fixed Factory Sealed Single Packaged
                  AC or Heat Pump Unit in a {room.square} m² room
                </div>
              </div>
              <div style={{ display: "flex", flexFlow: "row", padding: 15 }}>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "row",
                    fontSize: 18,
                    fontWeight: 100,
                    whiteSpace: "nowrap",
                  }}
                >
                  <span style={{ margin: "auto" }}>
                    {isOneDecimal(+calculations.OR.c22_c5.min.toFixed(2))} m²
                  </span>
                </div>
                <div style={{ fontSize: 12, margin: "auto 0 auto 10px" }}>
                  Minimum floor area for a Non-Fixed Factory Sealed Single
                  Packaged AC or Heat Pump Unit with a charge of {estimate} kg
                </div>
              </div>
            </div>
          )}
        <div
          style={{
            display: "flex",
            flexFlow: "row",
            boxShadow: "0 -5px 5px -5px rgba(0,0,0,.2)",
            minHeight: 300,
          }}
        >
          <div
            onMouseEnter={() => setTooltip("")}
            style={{
              position: "relative",
              width:
                showDataRange({ sealed: true, queue: "c1" }) !==
                  "No Charge Restriction" &&
                core.classification !== Types.Classification.IV
                  ? "50%"
                  : "100%",
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              flexFlow: "column",
              boxShadow: "5px 0 5px -5px rgba(0,0,0,.2)",
            }}
          >
            {core.classification === Types.Classification.IV && (
              <span style={{ padding: 10 }}>
                The ventilated enclosure shall fulfil the requirements of EN
                378-2:2016 and EN 378-3:2016
              </span>
            )}
            <div
              style={{
                color: "#808080",
                fontSize: 14,
                borderBottom: "1px solid #525C61",
                textTransform: "uppercase",
                maxWidth: "calc(100% - 30px)",
                width: "100%",
                paddingBottom: 5,
                margin: "0 auto",
                display: "block",
              }}
            >
              Maximum allowed charge under
            </div>
            {renderTable()}
          </div>
          {showDataRange({ sealed: true, queue: "c1" }) !==
            "No Charge Restriction" &&
            core.classification !== Types.Classification.IV && (
              <div
                onMouseEnter={() => setTooltip("")}
                style={{
                  position: "relative",
                  width: "50%",
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                  flexFlow: "column",
                  padding: 10,
                }}
              >
                <div
                  className="axis axis-y desktop-only"
                  style={{
                    transform: "rotate(-90deg)",
                    position: "absolute",
                    left: -58,
                    fontSize: 12,
                    color: "rgb(179, 179, 179)",
                  }}
                >
                  Refrigerant Charge (kg)
                </div>
                {core.application === Types.Application.HumanComfort ? (
                  <div
                    className="axis axis-x"
                    style={{
                      position: "absolute",
                      bottom: 5,
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontSize: 12,
                      color: "#B3B3B3",
                    
                    }}
                  >
                    Room Area (m²)
                  </div>
                ) : (
                  <div
                    className="axis axis-x"
                    style={{
                      position: "absolute",
                      bottom: 5,
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontSize: 12,
                      color: "#B3B3B3",
                    }}
                  >
                    Room Volume (m³)
                  </div>
                )}
                <FlexibleXYPlot
                  className="chart-container"
                  height={300}
                  yDomain={[0, base.m.m3 * 1.2]} // remains unchanged; correct for refrigerant charge
                  xDomain={xDomain}
                  onMouseLeave={() => setTooltip("")}
                >
                  <GradientDefs>
                    <linearGradient id="chart-M1" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#BCBCBC" stopOpacity={0.1} />
                      <stop
                        offset="100%"
                        stopColor="#BCBCBC"
                        stopOpacity={0.01}
                      />
                    </linearGradient>
                    <linearGradient id="chart-M2" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#BCBCBC" stopOpacity={0.1} />
                      <stop
                        offset="100%"
                        stopColor="#BCBCBC"
                        stopOpacity={0.01}
                      />
                    </linearGradient>
                    <linearGradient id="chart-C" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#B8AEEA" stopOpacity={1} />
                      <stop offset="100%" stopColor="#C9CCED" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="chart-D" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#4BA3D5" stopOpacity={1} />
                      <stop offset="100%" stopColor="#4BA3D5" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="chart-D2" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#69D6FF" stopOpacity={1} />
                      <stop offset="100%" stopColor="#69D6FF" stopOpacity={1} />
                    </linearGradient>
                    {/* comliance with, 2nd band, middle */}
                    <linearGradient id="chart-E" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#3D89C8" stopOpacity={1} />
                      <stop offset="100%" stopColor="#CADFEF" stopOpacity={1} />
                    </linearGradient>
                    {/* 1 additional measurement, last band, outter */}
                    <linearGradient id="chart-F" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#A6E2F6" stopOpacity={1} />
                      <stop offset="100%" stopColor="#E9F5FA" stopOpacity={1} />
                    </linearGradient>
                    {/* background - 2 additional measurements */}
                    <linearGradient id="chart-B" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#EDF6FA" stopOpacity={1} />
                      <stop offset="100%" stopColor="#EDF6FA" stopOpacity={1} />
                    </linearGradient>
                    {/* red - non-compliant */}
                    <linearGradient id="chart-N" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#f38796" stopOpacity={1} />
                      <stop offset="100%" stopColor="#f38796" stopOpacity={1} />
                    </linearGradient>
                    {/* top limit */}
                    <linearGradient id="chart-M3" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#F38796" stopOpacity={1} />
                      <stop offset="100%" stopColor="#F38796" stopOpacity={1} />
                    </linearGradient>
                  </GradientDefs>
                  <AreaSeries
                    animation
                    onSeriesMouseOver={() => setTooltip("B")}
                    onSeriesMouseOut={() => setTooltip("")}
                    className="chart-Back"
                    color={"url(#chart-B)"}
                    data={[
                      { x: data.F[0].x, y: base.m.m3 },
                      { x: data.F[data.F.length - 1].x, y: base.m.m3 },
                    ]}
                  />
                  {core.application === Types.Application.LessThanOnePerson &&
                  core.classification === Types.Classification.I &&
                  core.category === Types.Category.c ? (
                    <React.Fragment></React.Fragment>
                  ) : (
                    <AreaSeries
                      animation
                      strokeWidth={1}
                      stroke={tooltip === "F" ? "white" : "#00B6EF"}
                      onSeriesMouseOver={() => setTooltip("F")}
                      onSeriesMouseOut={() => setTooltip("")}
                      className="chart-F"
                      color={"url(#chart-F)"}
                      data={
                        core.application === Types.Application.HumanComfort
                          ? data.F2
                          : data.F
                      }
                    />
                  )}
                  {core.application === Types.Application.LessThanOnePerson &&
                  core.classification === Types.Classification.I &&
                  core.category === Types.Category.c ? (
                    <React.Fragment></React.Fragment>
                  ) : (
                    <AreaSeries
                      animation
                      strokeWidth={1}
                      stroke={tooltip === "E" ? "white" : "#005CB4"}
                      onSeriesMouseOver={() => setTooltip("E")}
                      onSeriesMouseOut={() => setTooltip("")}
                      className="chart-E"
                      color={"url(#chart-E)"}
                      data={
                        core.application === Types.Application.HumanComfort
                          ? data.E2
                          : data.E
                      }
                    />
                  )}
                  {((core.application !== Types.Application.HumanComfort &&
                    core.classification === Types.Classification.I) ||
                    (core.application === Types.Application.HumanComfort &&
                      core.classification === Types.Classification.I)) && (
                    <AreaSeries
                      animation
                      onSeriesMouseOver={() => setTooltip("N")}
                      onSeriesMouseOut={() => setTooltip("")}
                      className="chart-N"
                      color={"url(#chart-N)"}
                      data={[
                        {
                          x: data.F[0].x,
                          y: 60,
                        },
                        {
                          x: data.F[data.F.length - 1].x,
                          y: 60,
                        },
                      ]}
                    />
                  )}
                  {core.application !== Types.Application.HumanComfort ? (
                    core.classification !== Types.Classification.II && (
                      <AreaSeries
                        animation
                        strokeWidth={1}
                        stroke={
                          tooltip === "D" ? "white" : "rgba(255,255,255,0)"
                        }
                        onSeriesMouseOver={() => setTooltip("D")}
                        onSeriesMouseOut={() => setTooltip("")}
                        className="chart-D"
                        color={"url(#chart-D)"}
                        data={data.D}
                      />
                    )
                  ) : (
                    <AreaSeries
                      animation
                      strokeWidth={1}
                      stroke={
                        tooltip === "D2" ? "white" : "rgba(255,255,255,0)"
                      }
                      onSeriesMouseOver={() => setTooltip("D2")}
                      onSeriesMouseOut={() => setTooltip("")}
                      className="chart-D2"
                      color={"url(#chart-D)"}
                      data={data.D2}
                    />
                  )}

                  <AreaSeries
                    animation
                    onSeriesMouseOver={() => setTooltip("M3")}
                    onSeriesMouseOut={() => setTooltip("")}
                    className="chart-M3"
                    color={
                      (core.application !== Types.Application.HumanComfort &&
                        core.classification === Types.Classification.I) ||
                      (core.application === Types.Application.HumanComfort &&
                        core.classification === Types.Classification.I)
                        ? "url(#chart-N)"
                        : "url(#chart-M3)"
                    }
                    data={[
                      {
                        x: data.F[0].x,
                        y:
                          data.G[0] !== undefined
                            ? data.G[0].y + 10 > base.m.m3 + 10
                              ? data.G[0].y + 10
                              : base.m.m3 + 10
                            : base.m.m3 + 10,
                        y0: base.m.m3,
                      },
                      {
                        x: data.F[data.F.length - 1].x,
                        y:
                          data.G[0] !== undefined
                            ? data.G[0].y + 10 > base.m.m3 + 10
                              ? data.G[0].y + 10
                              : base.m.m3 + 10
                            : base.m.m3 + 10,
                        y0: base.m.m3,
                      },
                    ]}
                  />
                  {core.application !== Types.Application.HumanComfort ? (
                    data.D2[data.D2.length - 1].y > 10 && (
                      <AreaSeries
                        animation
                        stroke={tooltip === "D2" ? "white" : "#336e90"}
                        strokeWidth={0.5}
                        onSeriesMouseOver={() => setTooltip("D2")}
                        onSeriesMouseOut={() => setTooltip("")}
                        className="chart-D2"
                        color={"url(#chart-D2)"}
                        data={data.D2}
                      />
                    )
                  ) : (
                    <AreaSeries
                      animation
                      stroke={tooltip === "C2" ? "white" : "#EB69FF"}
                      strokeWidth={0.5}
                      onSeriesMouseOver={() => setTooltip("C2")}
                      onSeriesMouseOut={() => setTooltip("")}
                      className="chart-C2"
                      color={"url(#chart-C)"}
                      data={data.C2}
                    />
                  )}
                  {/* C/D2 Additional - no req needed */}
                  {core.application !== Types.Application.HumanComfort && (
                    <AreaSeries
                      animation
                      stroke={tooltip === "C" ? "white" : "#EB69FF"}
                      strokeWidth={0.5}
                      onSeriesMouseOver={() => setTooltip("C")}
                      onSeriesMouseOut={() => setTooltip("")}
                      className="chart-C"
                      color={"url(#chart-C)"}
                      data={data.C}
                    />
                  )}
                  <LineSeries
                    animation
                    stroke={"#000"}
                    strokeWidth={3}
                    className="chart-M2"
                    color={"url(#chart-M2)"}
                    style={{ strokeDasharray: "10 15" }}
                    data={[
                      { x: data.F[0].x, y: base.m.m2 },
                      { x: data.F[data.F.length - 1].x, y: base.m.m2 },
                    ]}
                  />
                  <LineSeries
                    animation
                    stroke={"#000"}
                    strokeWidth={3}
                    className="chart-M1"
                    color={"url(#chart-M1)"}
                    style={{ strokeDasharray: "10 15" }}
                    data={[
                      { x: data.F[0].x, y: base.m.m1 },
                      { x: data.F[data.F.length - 1].x, y: base.m.m1 },
                    ]}
                  />
   <LineSeries
  animation
  stroke={"#000"}
  strokeWidth={2}
  className="chart-G-line"
  data={[
    { x: getXAxisValueForGLine(), y: base.m.m1 },
    {
      x: getXAxisValueForGLine(),
      y:
        data.F[data.F.length - 1].y > base.m.m3
          ? data.F[data.F.length - 1].y
          : base.m.m3,
    },
  ]}
/>
<LabelSeries
  animation
  allowOffsetToBeReversed
  style={{ fill: "#000", fontSize: 13 }}
  data={[
    {
      x: getXAxisValueForLabels() + 3,
      y: calculateOffset(base.m.m1),
      label: "m₁ × 1.5",
      zIndex: "100",
    },
    {
      x: getXAxisValueForLabels() + 3,
      y: calculateOffset(base.m.m2),
      label: "m₂ × 1.5",
      zIndex: "100",
    },
    {
      x: getXAxisValueForLabels() + 6,
      y: calculateOffset(base.m.m3) +3,
      label: "m₃ × 1.5", 
      zIndex: "100",
    },
  ]}
/>
                  {/* MARK FROM HERE */}
                  {core.application === Types.Application.HumanComfort ? (
                    <MarkSeries
                      onSeriesMouseOver={() => setTooltip("G")}
                      onSeriesMouseOut={() => setTooltip("")}
                      animation
                      sizeRange={[0, 5]}
                      data={data.G2}
                      fill={"yellow"}
                      stroke={"black"}
                    />
                  ) : (
                    <MarkSeries
                      onSeriesMouseOver={() => setTooltip("G")}
                      onSeriesMouseOut={() => setTooltip("")}
                      animation
                      sizeRange={[0, 5]}
                      data={data.G}
                      fill={"yellow"}
                      stroke={"black"}
                    />
                  )}
                  <XAxis animation
                    tickFormat={formatRoomVolume} 
                    tickTotal={6}
                   /> 
                  <YAxis animation />
                </FlexibleXYPlot>
                <Portal>
                  <Tooltip
                    content={displayTooltip()}
                    visible={tooltip !== ""}
                    position={{ x: mousePos.x, y: mousePos.y }}
                  />
                </Portal>
              </div>
            )}


<Portal>
  {sealedPopup && (
    <div
      onClick={() => setSealedPopup(false)}
      style={{
        display: "block",
        position: "fixed",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        background: "#ffffff75",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          cursor: "default",
          background: "#C5D7E0",
          borderRadius: 5,
          padding: 50,
          fontSize: 13,
          fontWeight: 100,
          margin: 50,
          maxWidth: "calc(100% - 200px)",
          left: "calc(50% - 50px)",
          top: "calc(50% - 50px)",
          position: "absolute",
          transform: "translate(-50%, -50%)",
          display: "block",
          width: "calc(1440px - 100px)",
          maxHeight: "calc(100vh - 200px)",
          overflow: "auto",
          boxShadow: `
            0 2.8px 2.2px rgba(0, 0, 0, 0.02),
            0 6.7px 5.3px rgba(0, 0, 0, 0.028),
            0 12.5px 10px rgba(0, 0, 0, 0.035),
            0 22.3px 17.9px rgba(0, 0, 0, 0.042),
            0 41.8px 33.4px rgba(0, 0, 0, 0.05),
            0 100px 80px rgba(0, 0, 0, 0.07)`,
        }}
      >
        <b>Sealed System Information</b>
        <ul
          style={{
            listStyle: "initial",
            marginTop: 15,
            paddingLeft: 20,
          }}
        >
          <li>Refrigerating system in which all refrigerant containing parts are made tight by welding,
            brazing or a similar permanent connection which may include capped valves and capped service ports that allow proper repair or 
            disposal and which have a tested leakage rate of less than 3 grams per year under a pressure of 
            at least a quarter of the maximum allowable pressure</li>
          <li>Note 1 to entry: Joints based on mechanical forces which are prevented from improper use by the need of a special tool (e.g. by glue)
             are considered as a similar permanent connection.</li>
             <li>Note 2 to entry: Hermetically sealed systems in EN 16084 are equivalent to sealed systems in EN 378-2.</li>
          {/* Add more items specific to Sealed Systems here */}
        </ul>
      </div>
    </div>
  )}
</Portal>

          <Portal>
            {popup && (
              <div
                onClick={() => setPopup(false)}
                style={{
                  display: "block",
                  position: "fixed",
                  width: "100%",
                  height: "100%",
                  top: 0,
                  left: 0,
                  background: "#ffffff75",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    cursor: "default",
                    background: "#C5D7E0",

                    boxShadow: `
  0 2.8px 2.2px rgba(0, 0, 0, 0.02),
  0 6.7px 5.3px rgba(0, 0, 0, 0.028),
  0 12.5px 10px rgba(0, 0, 0, 0.035),
  0 22.3px 17.9px rgba(0, 0, 0, 0.042),
  0 41.8px 33.4px rgba(0, 0, 0, 0.05),
  0 100px 80px rgba(0, 0, 0, 0.07)`,
                  }}
                >
                  <b>
                  QLMV and QLAV charge values may only be used for location Class II in accordance with 5.3 where:

                  </b>
                  <ul
                    style={{
                      listStyle: "initial",
                      marginTop: 15,
                      paddingLeft: 20,
                    }}
                  >
                
                    <li>
      all connector (e.g. Headers, Tees, reducers, etc.) of refrigerant containing piping in occupied spaces are manufactured from factory made fittings or manifolds.
                    </li>
                    <li>
                    Site made joints and equipment of split systems installed in occupied space are in accordance with the instructions of the manufacturer of the factory made units. 
                    </li>
                    <li>
                    only valves and service ports can be installes in occupied spaces which are part of the factroy made units.
                    </li>
                    <li>
                      the heat exchanger in the indoor unit and the control of
                      the system are designed to prevent damage due to ice
                      formation
                    </li>
            
                    <li>
                    only permanent joints are used in the occupied space
                    except for site-made joints directly connecting the indoor 
                    unit to the piping
                    </li>
                    <li>
                    the refrigerant-containing pipes in the occupied space
                    are installed in such a way that it is protected against
                    accidental damage in accordance with EN378-2:2016, 6.2.3.3.4 and EN378-3:2016+A1:2020,6.2
                    </li>
                    <li>
                    alternative provisions to ensure safety are provided in accordance with C.3.2.2 and C.3.2.3; — 
                    appropriate measures include ventilation (natural or mechanical), safety shut-off valves and safety alarm, 
                    in conjunction with a gas detection device, see in EN 378-3:2016+A1:2020, Clauses 6, 8, 9 and 10. A safety 
                    alarm alone is not considered as an appropriate measure where occupants are restricted in their movement
                    (see EN 378-3:2016+A1:2020 8.1).
                    </li>
                    <li>
                    doors of the occupied space are not tight-fitting; and the effect of flow down is mitigated in accordance with EN378:2016 + A1:2020, C.3.2.4.
                    </li>
                    <li>
                    For occupied spaces exceeding 250 m², the charge limits calculation shall use 250 m² as the room floor area for determination of the room volume.
                    </li>
                  </ul>
                </div> 
              </div>
            )}
</Portal>
  </div>
</div>

<div
  className="print-only legend-data"
  style={{
    fontFamily: "Arial, sans-serif",
    color: "#333",
    fontSize: "11px",
    padding: "5px",
  }}

  
>
  
  


  {/* ====== CHART + RELATED DATA (AVOID PAGE BREAK) ====== */}
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "20px",
    
      marginBottom: "20px",
      pageBreakInside: "avoid", // prevents splitting this block across pages
    }}
  >
    
   
  

    {/* === APPLICATION & SYSTEM DETAILS COLUMN === */}
    <div
      style={{
        flex: "1 1 200px",
        minWidth: "170px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      {/* APPLICATION */}
      <div
        style={{
          border: "1px solid #E1E8ED",
          borderRadius: "4px",
          padding: "10px",
          backgroundColor: "#FAFCFE",
        }}
      >
        <h4
          style={{
            margin: "0 0 0px",
            fontSize: "13px",
            borderBottom: "1px solid #E1E8ED",
            paddingBottom: "14px",
          }}
        >
          Application
        </h4>
        <p style={{ margin: 0 }}>
          {core.application === Types.Application.HumanComfort
            ? "Human Comfort"
            : core.application === Types.Application.OtherApplications
            ? "Other Applications"
            : "<1 Person per 10 m²"}
        </p>
      </div>

      {/* SYSTEM DETAILS */}
      <div
        style={{
      
          border: "1px solid #E1E8ED",
          borderRadius: "4px",
          padding: "10px",
          backgroundColor: "#FAFCFE",
        }}
      >
        <h4
          style={{
            margin: "0 0 10px",
            fontSize: "13px",
            borderBottom: "1px solid #E1E8ED",
            paddingBottom: "5px",
          }}
        >
          System Details
        </h4>
        <div style={{ marginBottom: "5px" }}>
          <strong>Location classification:</strong>{" "}
          {Classification[core.classification - 1].id}
        </div>
        <div style={{ marginBottom: "5px" }}>
          <strong>Access category:</strong> {Category[core.category - 1].id}
        </div>
        <div style={{ marginBottom: "5px" }}>
          <strong>Refrigerant:</strong> Opteon™ {Refrigerant[core.refrigerant].id}
        </div>

        {/* M1, M2, M3 in a single vertical column */}
        <div style={{ marginTop: "10px" }}>
          <div style={{ marginBottom: "5px" }}>
            <strong>M1x1.5:</strong>{" "}
            {base.m.m1
              ? (Math.ceil(parseFloat(base.m.m1) * 100) / 100).toFixed(2)
              : "N/A"}{" "}
            kg
          </div>
          <div style={{ marginBottom: "5px" }}>
            <strong>M2x1.5:</strong>{" "}
            {base.m.m2
              ? (Math.ceil(parseFloat(base.m.m2) * 100) / 100).toFixed(2)
              : "N/A"}{" "}
            kg
          </div>
          <div>
            <strong>M3x1.5:</strong>{" "}
            {base.m.m3
              ? (Math.ceil(parseFloat(base.m.m3) * 100) / 100).toFixed(2)
              : "N/A"}{" "}
            kg
          </div>
        </div>
      </div>
    </div>
  </div>

 


  {/* =========== FLAMMABILITY + ROOM DIMENSIONS ROW (AVOID PAGE BREAK) =========== */}
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "20px",
      marginBottom: "20px",
      pageBreakInside: "avoid",
    }}
  >
    {/* FLAMMABILITY */}
    <div
      style={{
        flex: "1 1 300px",
        minWidth: "205px",
        border: "1px solid #E1E8ED",
        borderRadius: "4px",
        padding: "10px",
        backgroundColor: "#FAFCFE",
      }}
    >
      <h4
        style={{
          margin: "0 0 10px",
          fontSize: "13px",
          borderBottom: "1px solid #E1E8ED",
          paddingBottom: "5px",
        }}
      >
        Flammability
      </h4>
      <div style={{ marginBottom: "5px" }}>
        <strong>Lower flammability level:</strong> {base.lfl} kg/m³
      </div>
      <div>
      <strong>Upper flammability level:</strong>{" "}
{getUFL(core.refrigerant) !== "N/A" ? `${getUFL(core.refrigerant)} kg/m³` : "N/A"}
      </div>
    </div>

    {/* ROOM DIMENSIONS */}
    <div
      style={{
        flex: "1 1 300px",
        minWidth: "205px",
        border: "1px solid #E1E8ED",
        borderRadius: "4px",
        padding: "10px",
        backgroundColor: "#FAFCFE",
      }}
    >
      <h4
        style={{
          margin: "0 0 10px",
          fontSize: "13px",
          borderBottom: "1px solid #E1E8ED",
          paddingBottom: "5px",
        }}
      >
        Room Dimensions
      </h4>
      <div style={{ marginBottom: "5px" }}>
        <strong>Height:</strong> {room.height}m
      </div>
      <div style={{ marginBottom: "5px" }}>
        <strong>Width:</strong> {room.width}m
      </div>
      <div style={{ marginBottom: "5px" }}>
        <strong>Length:</strong> {room.length}m
      </div>
      <div>
        <strong>Volume:</strong> {room.cubic.toFixed(2)}m³
      </div>
      <div>
        <strong>Device Location:</strong> {getInstallationLabel()}
      </div>
    </div>

  </div>

  <div
    style={{
      border: "1px solid #E1E8ED",
      borderRadius: "4px",
      padding: "15px",
      backgroundColor: "#F5F9FC",
      marginBottom: "20px",
      pageBreakInside: "avoid",
    }}
  ><h3
  style={{
    margin: 0,
    fontSize: "14px",
    paddingBottom: "5px",
    borderBottom: "1px solid #E1E8ED",
  }}
>
  Chart Legend
</h3>
<div
  style={{
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    margin: "0 0 10px",
  }}
>
  <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
    <div>
      <span style={{ background: "#f38796", padding: "0 8px", border: "1px solid #ccc", marginRight: "3px", }}></span>{" "}
      Charge Size Not Compliant
    </div>
    <div>
      <span style={{ background: "#FFFF00", padding: "0 8px", border: "1px solid #ccc" , marginRight: "3px", }}></span>{" "}
      Refrigerant charge: {estimate}kg
    </div>
    <div>
      <span style={{ background: "#EDF6FA", padding: "0 8px", border: "1px solid #ccc", marginRight: "3px",  }}></span>{" "}
      2 Additional Protection Measures + Leak Detection (EN 378-1:2016,
      C.3.2.2)
    </div>
    <div>
      <span style={{ background: "#A6E2F6", padding: "0 8px", border: "1px solid #ccc" , marginRight: "3px", }}></span>{" "}
      1 Additional Protective Measure + Leak Detection (EN 378-1:2016,
      C.3.2.2)
    </div>
  </div>
  <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
    <div>
      <span style={{ background: "#3D89C8", padding: "0 8px", border: "1px solid #ccc", marginRight: "3px",  }}></span>{" "}
      Compliance with EN 378-1:2016, C.3.1 & C3.2
    </div>
    <div>
      <span style={{ background: "#69D6FF", padding: "0 8px", border: "1px solid #ccc" , marginRight: "3px", }}></span>{" "}
      EN 378-3:2016, 4.2 open air or 4.3 machinery room construction
      requirements apply
    </div>
    <div>
      <span style={{ background: "#B8AEEA", padding: "0 8px", border: "1px solid #ccc", marginRight: "3px",  }}></span>{" "}
      No additional protective measures required
    </div>
  </div>
</div>
    
  </div>

  {/* =========== CHART LEGEND (AVOID PAGE BREAK IF NEEDED) =========== */}
   </div>


  
   <div
        className="disclaimer"
        style={{
          maxWidth: 700,
          margin: "0px auto",
          padding: "0 30px",
          paddingTop: "1em",
          fontSize: 10,
          color: "#B3B3B3",
          fontWeight: "bold",
        }}
      >
        DISCLAIMER
        <br />
        {Disclaimer}
      </div>


      <div className="buttons">
        <div onClick={() => prev()}>Back</div>
        <div onClick={() => window.print()}>Export to PDF</div>
        <div onClick={() => onClick(0)}>New Calculation</div>
      </div>
 
    </div>
  );
};